import { useEffect, useState } from 'react';
import { AlertTriangle, CreditCard, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { SubscriptionWithPlan } from '../../lib/subscriptions';
import { createCustomerPortalSession } from '../../lib/payments';
import { logger } from '../../lib/logger';

const DISMISSED_INVOICE_KEY = 'billing_status_banner_dismissed_invoice_id';

interface LatestOpenInvoice {
  id: string;
  stripe_invoice_id: string | null;
  due_date: string | null;
  invoice_number: string | null;
}

interface BillingStatusBannerProps {
  userId: string;
  subscription: SubscriptionWithPlan | null;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function getDismissedInvoiceId(): string | null {
  try {
    return window.localStorage.getItem(DISMISSED_INVOICE_KEY);
  } catch {
    return null;
  }
}

function setDismissedInvoiceId(invoiceId: string): void {
  try {
    window.localStorage.setItem(DISMISSED_INVOICE_KEY, invoiceId);
  } catch {
    // No-op for storage restrictions.
  }
}

export function BillingStatusBanner({ userId, subscription }: BillingStatusBannerProps) {
  const [latestOpenInvoice, setLatestOpenInvoice] = useState<LatestOpenInvoice | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const isPastDue = subscription?.status === 'past_due';

  useEffect(() => {
    if (!isPastDue || !userId) {
      setLatestOpenInvoice(null);
      setIsDismissed(false);
      return;
    }

    let isActive = true;
    const loadLatestInvoice = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('subscription_invoices')
          .select('id,stripe_invoice_id,due_date,invoice_number')
          .eq('user_id', userId)
          .eq('status', 'open')
          .order('invoice_date', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!isActive) return;
        const latest = (data as LatestOpenInvoice | null) || null;
        setLatestOpenInvoice(latest);

        const currentInvoiceKey = latest?.stripe_invoice_id || latest?.id || null;
        if (currentInvoiceKey) {
          setIsDismissed(getDismissedInvoiceId() === currentInvoiceKey);
        } else {
          setIsDismissed(false);
        }
      } catch (error) {
        logger.error('Failed to load latest open invoice for billing banner', {
          error: error instanceof Error ? error.message : String(error),
        });
        if (isActive) {
          setLatestOpenInvoice(null);
          setIsDismissed(false);
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadLatestInvoice();
    return () => {
      isActive = false;
    };
  }, [isPastDue, userId]);

  const handleDismiss = () => {
    const dismissKey = latestOpenInvoice?.stripe_invoice_id || latestOpenInvoice?.id;
    if (dismissKey) {
      setDismissedInvoiceId(dismissKey);
    }
    setIsDismissed(true);
  };

  const handleUpdatePaymentMethod = async () => {
    setIsOpeningPortal(true);
    try {
      const portalUrl = await createCustomerPortalSession(userId, `${window.location.origin}/profile?tab=subscription`);
      window.location.href = portalUrl;
    } catch (error) {
      logger.error('Failed to open Stripe billing portal from status banner', {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsOpeningPortal(false);
    }
  };

  if (!isPastDue || isDismissed) {
    return null;
  }

  return (
    <div className="sticky top-0 z-20 px-4 md:px-6 pt-4">
      <div className="bg-terracotta/10 border border-terracotta/30 rounded-2xl p-4 md:p-5 shadow-soft">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-terracotta/20 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-terracotta" />
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium text-charcoal">Payment failed for your subscription</p>
            <p className="text-sm text-charcoal/75 font-light mt-1">
              Your account is still active, but your plan is in past due status.
              {latestOpenInvoice?.due_date ? ` Next retry: ${formatDate(latestOpenInvoice.due_date)}.` : ' Update your payment method to avoid cancellation.'}
            </p>
          </div>

          <button
            onClick={handleDismiss}
            className="w-8 h-8 rounded-full bg-white/70 border border-stone flex items-center justify-center text-sand hover:text-charcoal hover:border-charcoal/30 transition-all duration-300"
            aria-label="Dismiss billing warning"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3">
          {latestOpenInvoice?.invoice_number ? (
            <p className="text-xs font-mono uppercase tracking-widest text-sand">
              Invoice #{latestOpenInvoice.invoice_number}
            </p>
          ) : (
            <span />
          )}

          <button
            onClick={handleUpdatePaymentMethod}
            disabled={isOpeningPortal || isLoading}
            className="inline-flex items-center gap-2 bg-terracotta text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-terracotta/90 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <CreditCard className="w-4 h-4" />
            {isOpeningPortal ? 'Opening...' : 'Update payment method'}
          </button>
        </div>
      </div>
    </div>
  );
}
